import type { ShadingModel } from './lib/types';

/**
 * Runtime configuration.
 *
 * The Mapbox token and style below are carried over from the 2023 site and
 * belong to a third-party Mapbox account ("jgf94"), not to Cornell. They are
 * public (pk.*) tokens, which are safe to ship in client code, but the account
 * holder can revoke them at any time. Override via .env to migrate:
 *   VITE_MAPBOX_TOKEN=...
 *   VITE_MAPBOX_STYLE=...
 */
export const MAPBOX_TOKEN =
  import.meta.env.VITE_MAPBOX_TOKEN ??
  'pk.eyJ1IjoiamdmOTQiLCJhIjoiY2thaXk2bjQzMDZvYzJ3cXoxeThnODU5NyJ9.o1ijddB0igPdlsWMw6iRVw';

export const MAPBOX_STYLE =
  import.meta.env.VITE_MAPBOX_STYLE ?? 'mapbox://styles/jgf94/ckg7ai9oo06zj19p6zw741oe2';

/**
 * Per-tree LiDAR point clouds live in the frozen 2023 bucket. In production
 * CloudFront routes /data/* there; in dev we hit the bucket directly (its CORS
 * policy already allows any origin).
 */
export const DATA_BASE = import.meta.env.DEV
  ? 'https://s3.amazonaws.com/www.treefolio.org-2.0/data'
  : '/data';

/** Vector tiles built by pipeline/build-tiles.sh. Served by vite in dev. */
export const TILE_BASE = '/tiles';

/**
 * Cache-busting stamp appended to every tile URL, set by deploy.sh from the
 * newest tile's mtime.
 *
 * Tiles are uploaded `immutable, max-age=31536000` at stable z/x/y paths, which
 * is right for their access pattern but means a rebuilt tileset is invisible to
 * anyone who has already loaded it: the URL has not changed, and `immutable`
 * tells the browser not even to revalidate for a year. A CloudFront
 * invalidation does nothing about this -- it clears the edge, not the client.
 *
 * This bit me for real: after retiling with the has_canopy attribute, a browser
 * that had visited before kept serving the old tiles, so every tree came back
 * without the flag and the app requested point clouds it should have skipped.
 * It looked exactly like a stale deploy, and the tile bytes in S3 were correct
 * the whole time.
 *
 * Changing the stamp changes every tile URL, so returning visitors refetch once.
 */
export const TILE_VERSION = import.meta.env.VITE_TILE_VERSION ?? 'dev';

export const INITIAL_VIEW = {
  center: [-73.9867, 40.6845] as [number, number],
  zoom: 10,
  pitch: 15,
  bearing: 0,
};

/**
 * Zoom from which building footprints and massing are drawn.
 *
 * Lowered from 15 to meet OVERVIEW_ZOOM, where the tree tiles already hand off
 * to individual points. Buildings were arriving two zooms later than the trees
 * they shade, so the middle zooms showed canopy floating over a blank basemap.
 * The building tiles cover z13 already, so this costs no new data -- measured
 * over Midtown at z13, the viewport query and footprint extraction take 24 ms
 * for 14,475 features, and no canopy is loaded there to classify against them.
 */
export const DETAIL_ZOOM = 13;
/** Zoom at which the city-wide overview hands off to individual tree points. */
export const OVERVIEW_ZOOM = 13;

/**
 * Cap on simultaneously loaded point clouds. Each is 60-880 KB of LiDAR and is
 * reclassified on every slider move, so this bounds both memory and CPU.
 */
export const MAX_SELECTED_TREES = 50;

/**
 * Zoom from which canopies load without being clicked.
 *
 * The per-tree JSON format is what sets this. Measured at 1440x900 over
 * Midtown, against a mean point cloud of 180 KB / 5,773 points:
 *
 *   z19      5 trees    0.9 MB
 *   z18     21 trees    3.8 MB
 *   z17    102 trees     18 MB   <- capped to AUTO_TREE_CAP nearest
 *   z16    800 trees    144 MB
 *   z15  1,513 trees    272 MB
 *
 * z17 is the last zoom where this is defensible, and only with a cap. Below it
 * the app stays click-to-select. Lifting the limit properly means tiling the
 * point clouds themselves (105 GB, 3.28 billion points), which is a separate
 * piece of work.
 *
 * Tried at 16 and reverted. This number is not independent of AUTO_TREE_CAP:
 * 17 is precisely the zoom where the trees in view stop outnumbering the cap.
 * Measured over Astoria, trees with a canopy in the viewport at 1440x900 --
 * z18: 42, z17: 243, z16: 1,180, z15: 1,523. At 17 the cap covers everything
 * on screen; at 16 it covers a fifth, and the nearest-first ordering renders
 * that as an island of canopy in the middle ringed by bare circles, which
 * reads as broken rather than as still loading. Moving this down means raising
 * the cap to match, and the cap is already the ceiling (see AUTO_TREE_CAP).
 */
export const AUTO_LOAD_ZOOM = 17;

/**
 * Ceiling on auto-loaded canopies at AUTO_LOAD_ZOOM and above.
 *
 * Loading runs nearest-to-centre first and reports each cloud as it lands, so
 * the map fills outward from the middle rather than appearing all at once. The
 * cap only exists to bound memory and per-frame classification cost -- at 250
 * trees that is roughly 550k points, about 30 MB of typed arrays, and still
 * inside a frame with the hull index.
 *
 * Set too low this is very visible: a dense cluster in the centre ringed by
 * empty circles, which reads as broken rather than as still loading.
 *
 * What used to bound this was the main thread. Every sun-slider move
 * reclassifies the whole selection, and a nine-step scrub spent 85% of its wall
 * time in long tasks. Classification now runs in a worker (lib/canopyClient.ts),
 * and the same scrub produces none at all. Measured over Greenpoint at z17, 250
 * trees / 392,982 points, 21 June, nine steps 250 ms apart:
 *
 *              long tasks   share of wall time   longest
 *   main thread        62                  85%     96 ms
 *   worker              0                   0%         -
 *
 * So the ceiling is no longer jank; it is how stale the figures are allowed to
 * get, since only one classification runs at a time. Worker turnaround for that
 * same selection, slider move to updated panel:
 *
 *   11:00 (sun 68 deg)   151 ms
 *   13:00 (sun 72 deg)   220 ms
 *   18:30 (sun 25 deg)   451 ms
 *   19:24 (sun 10 deg)   949 ms
 *
 * Cost climbs as the sun drops because the shadow hulls lengthen and every
 * canopy point has more candidates to test -- that is broad-phase work, and it
 * is where a height-banded index (see to-do/BUILDINGS-BLOCK-SHADE.md) would pay,
 * not in the narrow phase. Raising the cap multiplies all of it: a z16 viewport
 * needs roughly 1,180 trees, 4.7x this, which is a second of latency at midday
 * and about 200 MB of per-tree JSON to fetch. Left at 250 until the low-sun
 * broad phase is cheaper.
 */
const AUTO_TREE_CAP_DESKTOP = 250;

/**
 * The same ceiling for a phone.
 *
 * This is the half of "unusable on a phone" that no amount of layout fixes:
 * 250 canopies is roughly 45 MB of per-tree JSON, which on a cellular
 * connection is a minute of loading and a chunk of someone's data allowance,
 * and about 520k points to reclassify on every touch of the sun arc. The
 * desktop figure was set against a machine that can absorb 170-190ms tasks; a
 * phone SoC on battery cannot, and the arc is the app's primary interaction.
 *
 * 60 keeps the properties the cap exists to protect -- nearest-first fill, no
 * ring of empty circles around a loaded island. A 390x844 viewport at z17 holds
 * far fewer trees than the 1440x900 the desktop cap was measured against, so
 * this is a smaller cut in coverage than the ratio makes it look.
 */
const AUTO_TREE_CAP_PHONE = 60;

/**
 * Chosen once at load. Rotating a phone does not change what it can afford, and
 * re-deciding mid-session would drop canopies already on screen.
 */
export const AUTO_TREE_CAP = (() => {
  if (typeof window === 'undefined') return AUTO_TREE_CAP_DESKTOP;

  const coarse = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  const small = Math.min(window.screen.width, window.screen.height) < 820;
  // Both are advisory and absent on Safari, so they can only ever confirm.
  const memory = (navigator as { deviceMemory?: number }).deviceMemory ?? 8;
  const cores = navigator.hardwareConcurrency ?? 8;

  return (coarse && small) || memory <= 4 || cores <= 4
    ? AUTO_TREE_CAP_PHONE
    : AUTO_TREE_CAP_DESKTOP;
})();

/**
 * Which model classifies canopy points against buildings.
 *
 * `occluded` (default) is height-aware: it asks whether a building actually
 * stands between the point and the sun, and stops a shadow at the first
 * building it meets. `2023` is the published flat projection, kept selectable
 * with `?model=2023` so any figure in the paper can be reproduced on demand.
 *
 * The two disagree substantially -- most at high sun, where the height term
 * bites hardest -- so StatsPanel names the model alongside the percentages
 * whenever `2023` is in force. The default needs no such note: it is the only
 * model a visitor can reach without editing the URL, and the site does not owe
 * every reader a footnote about an alternative they did not ask for.
 */
export const SHADING_MODEL: ShadingModel = (() => {
  if (typeof window === 'undefined') return 'occluded';
  return new URLSearchParams(window.location.search).get('model') === '2023'
    ? '2023'
    : 'occluded';
})();

/**
 * Draw the ground shadow polygons behind the canopy.
 *
 * Off. These are convex hulls of each footprint together with its projection,
 * so a stepped or L-shaped building yields a large wedge that corresponds to no
 * real shadow. Acceptable as a hint when one building was selected; at hundreds
 * of buildings they read as solid objects and are simply wrong.
 *
 * Still off under the occluded model. That model no longer *believes* the
 * wedge -- it clips the sun ray against the true footprint, so the hulls are
 * now only a broad phase -- but they are the same convex wedges on screen, so
 * drawing one would show a shadow the analysis disagrees with. Turning this
 * back on for a focused building means emitting the real swept polygon, which
 * is a separate piece of work from classification.
 */
export const DRAW_SHADOW_HULLS = false;

/** Parallel point cloud fetches. Enough to saturate, few enough to stay polite. */
export const FETCH_CONCURRENCY = 8;

/**
 * How often newly arrived canopies are committed to the scene. Every commit
 * reclassifies the whole selection, so this trades a little latency for not
 * doing that work once per tree.
 */
export const AUTO_BATCH_MS = 350;

/** NYC reference point for sunrise/sunset, matching the 2023 implementation. */
export const CITY_LAT = 40.7;
export const CITY_LON = -73.9;
